import { useEffect, useState } from 'react';
import './chatBotDataForm.css';
import { toast } from 'react-toastify';
import fileUpload from '../../../utils/fileUpload';
import CloseIcon from '../../../assets/icons/trvto_close.svg';

// WhatsApp accepts only JPG/PNG images up to 5 MB
const WHATSAPP_IMAGE_TYPES = ['image/jpeg', 'image/png'];
const WHATSAPP_IMAGE_MAX_MB = 5;
const INSTAGRAM_IMAGE_MAX_MB = 8;

// Give each upload a unique name so it never overwrites another entry's
// image in Firebase (which would break that entry's saved URL).
const uniqueFile = (file) => new File([file], `${Date.now()}-${file.name.replace(/\s+/g, '-')}`, { type: file.type });

const initialState = {
    type: '',
    name: '',
    aliases: '',
    isActive: true,

    // WhatsApp / Instagram
    development: '',
    production: '',
    instagramMedia: '',
    whatsappImageUrl: '',
    whatsAppImage: null,
    instagramImage: null,

    // Special offer
    bannerImage: '',
    bannerImageFile: null,
    tripIds: '',
    startDate: '',
    endDate: ''
};

const ChatBotDataForm = ({ editData, type, loading, onSubmit, onClose }) => {
    const [formData, setFormData] = useState(initialState);
    const [uploading, setUploading] = useState(false);

    // =========================================================
    // EDIT / ADD MODE
    // =========================================================

    useEffect(() => {
        if (editData) {
            setFormData({
                type: editData.type || '',
                name: editData.name || '',
                aliases: editData.aliases?.join(', ') || '',
                isActive: editData.isActive ?? true,

                development: editData?.mediaIds?.development || '',

                production: editData?.mediaIds?.production || '',

                instagramMedia: editData?.instagramMedia || '',

                // Saved WhatsApp image (permanent copy)
                whatsappImageUrl: editData?.whatsappImageUrl || '',
                whatsAppImage: null,
                instagramImage: null,

                // Existing uploaded banner URL
                bannerImage: editData?.bannerImage || '',

                // New file starts empty
                bannerImageFile: null,

                tripIds: editData?.tripIds?.join(', ') || '',

                startDate: editData?.startDate ? new Date(editData.startDate).toISOString().split('T')[0] : '',

                endDate: editData?.endDate ? new Date(editData.endDate).toISOString().split('T')[0] : ''
            });

            return;
        }

        if (type) {
            setFormData({
                ...initialState,
                type
            });

            return;
        }

        setFormData(initialState);
    }, [editData, type]);

    // =========================================================
    // HANDLE INPUT CHANGE
    // =========================================================

    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        // File input
        if (type === 'file') {
            const file = files?.[0] || null;

            if (file && name === 'whatsAppImage') {
                if (!WHATSAPP_IMAGE_TYPES.includes(file.type)) {
                    toast.error('WhatsApp image must be a JPG or PNG.');
                    e.target.value = '';
                    return;
                }
                if (file.size > WHATSAPP_IMAGE_MAX_MB * 1024 * 1024) {
                    toast.error(`WhatsApp image must be ${WHATSAPP_IMAGE_MAX_MB} MB or smaller.`);
                    e.target.value = '';
                    return;
                }
            }

            if (file && name === 'instagramImage' && file.size > INSTAGRAM_IMAGE_MAX_MB * 1024 * 1024) {
                toast.error(`Instagram image must be ${INSTAGRAM_IMAGE_MAX_MB} MB or smaller.`);
                e.target.value = '';
                return;
            }

            setFormData((prev) => ({
                ...prev,
                [name]: file
            }));

            return;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setUploading(true);
            const data = { ...formData };

            // =====================================================
            // SPECIAL OFFER
            // =====================================================

            if (data.type === 'special_offer') {
                data.tripIds = data.tripIds ? data.tripIds : '';

                // -----------------------------------------------
                // Upload banner image if a new file is selected
                // -----------------------------------------------

                if (data.bannerImageFile) {
                    const bannerUrl = await fileUpload(uniqueFile(data.bannerImageFile), 'chatbot', 'create', 'bannerImage');

                    data.bannerImage = bannerUrl;
                }

                // File object should never be sent to backend
                delete data.bannerImageFile;
            }

            // =====================================================
            // NON SPECIAL OFFER
            // =====================================================
            else {
                delete data.bannerImage;
                delete data.bannerImageFile;
                delete data.tripIds;
                delete data.startDate;
                delete data.endDate;
            }

            // =====================================================
            // INSTAGRAM IMAGE
            // =====================================================

            if (data.instagramImage) {
                const instagramUrl = await fileUpload(uniqueFile(data.instagramImage), 'chatbot', 'create', 'instagramImg');

                data.instagramMedia = instagramUrl;
            }
            delete data.instagramImage;

            // =====================================================
            // WHATSAPP IMAGE
            // =====================================================

            // The FILE itself is sent to the backend (field "image"), which
            // uploads it to WhatsApp and stores the media id. We also keep a
            // permanent copy in Firebase, because WhatsApp deletes uploaded
            // media after 30 days and the backend re-uploads it from this URL.
            if (data.whatsAppImage) {
                data.whatsappImageUrl = await fileUpload(uniqueFile(data.whatsAppImage), 'chatbot', 'create', 'whatsappImg');
            }

            // =====================================================
            // SUBMIT
            // =====================================================

            await onSubmit(data);
        } catch (error) {
            console.error('ChatBotDataForm submit error:', error);
            toast.error('Image upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const busy = loading || uploading;

    const isSpecialOffer = formData.type === 'special_offer';

    // =========================================================
    // UI
    // =========================================================

    return (
        <div className='cbdf-card'>
            {/* =================================================
                HEADER
            ================================================= */}

            <div className='cbdf-header'>
                <div>
                    <h3>{editData ? 'Edit Data' : 'Add Data'}</h3>

                    <p>{editData ? 'Update chatbot information.' : 'Create a new chatbot item.'}</p>
                </div>

                <img
                    src={CloseIcon}
                    alt='close'
                    width={30}
                    height={30}
                    onClick={onClose}
                    style={{
                        cursor: 'pointer'
                    }}
                />
            </div>

            {/* =================================================
                FORM
            ================================================= */}

            <form className='cbdf-form' onSubmit={handleSubmit}>
                {/* =================================================
                    TYPE
                ================================================= */}

                <div className='cbdf-field'>
                    <label>Type</label>

                    <select name='type' value={formData.type} onChange={handleChange} required>
                        <option value=''>Select Type</option>

                        <option value='city'>City</option>

                        <option value='destination'>Destination</option>

                        <option value='city_and_destination'>City And Destination</option>

                        <option value='tripType'>Trip Type</option>

                        <option value='offer'>Offer</option>

                        <option value='special_offer'>Special Offer</option>
                    </select>
                </div>

                {/* =================================================
                    NAME
                ================================================= */}

                <div className='cbdf-field'>
                    <label>Name</label>

                    <input
                        type='text'
                        name='name'
                        placeholder='Enter name'
                        value={formData.name}
                        onChange={handleChange}
                        required
                    />
                </div>

                {/* =================================================
                    ALIASES
                ================================================= */}

                <div className='cbdf-field'>
                    <label>Aliases</label>

                    <textarea
                        rows='4'
                        name='aliases'
                        placeholder='Example: Bengaluru, Bangalore, BLR'
                        value={formData.aliases}
                        onChange={handleChange}
                    />

                    <small>Separate aliases with commas.</small>
                </div>

                {/* =================================================
                    SPECIAL OFFER
                ================================================= */}

                {isSpecialOffer && (
                    <>
                        <div className='cbdf-special-offer-section'>
                            <h4>Special Offer Details</h4>

                            <p>Configure the banner, selected trips and offer duration.</p>
                        </div>

                        {/* =========================================
                            BANNER IMAGE
                        ========================================= */}

                        <div className='cbdf-field'>
                            <label>Special Offer Banner Image</label>

                            <input type='file' name='bannerImageFile' accept='image/*' onChange={handleChange} />

                            {/* New selected image */}
                            {formData.bannerImageFile && (
                                <div
                                    className='cbdf-image-preview'
                                    style={{
                                        marginTop: '10px'
                                    }}
                                >
                                    <img
                                        src={URL.createObjectURL(formData.bannerImageFile)}
                                        alt='Selected banner'
                                        style={{
                                            width: '100%',
                                            maxHeight: '220px',
                                            objectFit: 'cover',
                                            borderRadius: '8px'
                                        }}
                                    />

                                    <small>New banner selected</small>
                                </div>
                            )}

                            {/* Existing image when editing */}
                            {!formData.bannerImageFile && formData.bannerImage && (
                                <div
                                    className='cbdf-image-preview'
                                    style={{
                                        marginTop: '10px'
                                    }}
                                >
                                    <img
                                        src={formData.bannerImage}
                                        alt='Current banner'
                                        style={{
                                            width: '100%',
                                            maxHeight: '220px',
                                            objectFit: 'cover',
                                            borderRadius: '8px'
                                        }}
                                    />

                                    <small>Current banner</small>
                                </div>
                            )}

                            <small>
                                Select the image that will appear on the TRVTO homepage special offer section.
                            </small>
                        </div>

                        {/* =========================================
                            TRIP IDS
                        ========================================= */}

                        <div className='cbdf-field'>
                            <label>Special Offer Trips</label>

                            <textarea
                                rows='4'
                                name='tripIds'
                                placeholder='Enter Trip IDs separated by commas'
                                value={formData.tripIds}
                                onChange={handleChange}
                                required
                            />

                            <small>Example: 68a123..., 68a456..., 68a789...</small>
                        </div>

                        {/* =========================================
                            START DATE
                        ========================================= */}

                        <div className='cbdf-field'>
                            <label>Start Date</label>

                            <input
                                type='date'
                                name='startDate'
                                value={formData.startDate}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {/* =========================================
                            END DATE
                        ========================================= */}

                        <div className='cbdf-field'>
                            <label>End Date</label>

                            <input
                                type='date'
                                name='endDate'
                                value={formData.endDate}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </>
                )}

                {/* =================================================
                    WHATSAPP DEVELOPMENT MEDIA
                ================================================= */}

                <div className='cbdf-field'>
                    <label>WhatsApp Dev Media ID</label>

                    <input
                        type='text'
                        name='development'
                        placeholder='WhatsApp Dev Media ID'
                        value={formData.development}
                        onChange={handleChange}
                    />
                </div>

                {/* =================================================
                    WHATSAPP PRODUCTION MEDIA
                ================================================= */}

                <div className='cbdf-field'>
                    <label>WhatsApp Prod Media ID</label>

                    <input
                        type='text'
                        name='production'
                        placeholder='WhatsApp Prod Media ID'
                        value={formData.production}
                        onChange={handleChange}
                    />
                </div>

                {/* =================================================
                    INSTAGRAM IMAGE URL
                ================================================= */}

                <div className='cbdf-field'>
                    <label>Instagram Image URL</label>

                    <input
                        type='text'
                        name='instagramMedia'
                        placeholder='Instagram Image URL'
                        value={formData.instagramMedia}
                        onChange={handleChange}
                    />
                </div>

                {/* =================================================
                    WHATSAPP IMAGE
                ================================================= */}

                <div className='cbdf-field'>
                    <label>WhatsApp Image</label>

                    <input type='file' name='whatsAppImage' accept='image/jpeg,image/png' onChange={handleChange} />

                    {(formData.whatsAppImage || formData.whatsappImageUrl) && (
                        <div className='cbdf-image-preview' style={{ marginTop: '10px' }}>
                            <img
                                src={
                                    formData.whatsAppImage
                                        ? URL.createObjectURL(formData.whatsAppImage)
                                        : formData.whatsappImageUrl
                                }
                                alt='WhatsApp'
                                style={{ width: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '8px' }}
                            />
                            <small>{formData.whatsAppImage ? 'New image — uploaded to WhatsApp on save' : 'Current image'}</small>
                        </div>
                    )}

                    <small>JPG or PNG, max {WHATSAPP_IMAGE_MAX_MB} MB. The Media ID is filled in automatically.</small>
                </div>

                {/* =================================================
                    INSTAGRAM IMAGE
                ================================================= */}

                <div className='cbdf-field'>
                    <label>Instagram Image</label>

                    <input type='file' name='instagramImage' accept='image/jpeg,image/png' onChange={handleChange} />
                    <small>Optional — the WhatsApp image is used if this is empty.</small>
                </div>

                {/* =================================================
                    ACTIVE
                ================================================= */}

                <label className='cbdf-checkbox'>
                    <input type='checkbox' name='isActive' checked={formData.isActive} onChange={handleChange} />
                    Active
                </label>

                {/* =================================================
                    FOOTER
                ================================================= */}

                <div className='cbdf-footer'>
                    <button type='button' className='cbdf-cancel' onClick={onClose}>
                        Cancel
                    </button>

                    <button type='submit' className='cbdf-save' disabled={busy}>
                        {uploading && !loading ? 'Uploading image...' : loading ? 'Saving...' : editData ? 'Update' : 'Save'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ChatBotDataForm;
